// Basemap tiles are served from OUR origin (/tiles/...), proxied and cached by
// the frontend nginx (see frontend/nginx.conf.template). The provider behind
// it is an nginx env setting, so nothing here names CARTO/OSM directly: the
// manifest tells us which attribution to show, how deep we may zoom, and
// whether "dark" tiles are really a light style that needs CSS inverting.
import { useEffect, useState } from 'react';

export const TILE_URL_LIGHT = '/tiles/light/{z}/{x}/{y}.png';
export const TILE_URL_DARK = '/tiles/dark/{z}/{x}/{y}.png';

const OSM_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const ATTRIBUTION = {
  osm: OSM_ATTR,
  carto: `${OSM_ATTR} &copy; <a href="https://carto.com/attributions">CARTO</a>`,
};

// Matches the Dockerfile defaults, so a failed manifest fetch still renders
// a correct map.
const DEFAULT_CONFIG = { provider: 'osm', darkCssFilter: true, maxZoom: 19 };

let manifestPromise = null;
function loadManifest() {
  if (!manifestPromise) {
    manifestPromise = fetch('/tiles/manifest.json')
      .then(r => (r.ok ? r.json() : null))
      .then(j => (j && typeof j === 'object' ? { ...DEFAULT_CONFIG, ...j } : DEFAULT_CONFIG))
      .catch(() => DEFAULT_CONFIG);
  }
  return manifestPromise;
}

export function useTileConfig() {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  useEffect(() => {
    let alive = true;
    loadManifest().then(c => { if (alive) setConfig(c); });
    return () => { alive = false; };
  }, []);
  return config;
}

// Props for a react-leaflet <TileLayer>. `key` forces a remount when the URL
// or filter class changes (Leaflet does not re-read these options live).
export function tileLayerProps(darkMode, config = DEFAULT_CONFIG) {
  const url = darkMode ? TILE_URL_DARK : TILE_URL_LIGHT;
  const className = darkMode && config.darkCssFilter ? 'ol-tiles-dark-filter' : '';
  return {
    key: `${url}|${className}`,
    url,
    className,
    maxZoom: config.maxZoom,
    attribution: ATTRIBUTION[config.provider] || ATTRIBUTION.osm,
  };
}
